import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, Radio, Trash2, RotateCw, Film } from "lucide-react";
import { fmt } from "../lib/format";

const STATUS_META = {
  pending: { label: "Queued", cls: "text-muted-foreground" },
  downloading: { label: "Fetching", cls: "text-accent" },
  transcribing: { label: "Transcribing", cls: "text-accent" },
  analyzing: { label: "Analyzing", cls: "text-accent" },
  cutting: { label: "Cutting Clips", cls: "text-accent" },
  completed: { label: "Ready", cls: "text-accent" },
  failed: { label: "Failed", cls: "text-primary" },
};

export const ShowRow = ({ show, active, onSelect, onDelete, onRetry }) => {
  const meta = STATUS_META[show.status] || STATUS_META.pending;
  const working = !["completed", "failed"].includes(show.status);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      data-testid={`show-row-${show.id}`}
      onClick={() => onSelect(show.id)}
      className={`group relative cursor-pointer border-l-2 px-5 py-4 transition-colors ${
        active ? "border-l-primary bg-secondary/60" : "border-l-transparent hover:bg-secondary/30"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate font-body font-semibold text-sm text-white">
            {show.is_video && <Film className="h-3.5 w-3.5 shrink-0 text-accent" />}
            {show.title}
          </p>
          <div className="mt-1 flex items-center gap-2 font-mono text-[11px]">
            <span className={meta.cls}>{meta.label}</span>
            {show.duration_sec > 0 && <span className="text-muted-foreground">· {fmt(show.duration_sec)}</span>}
            {show.status === "completed" && (
              <span className="text-muted-foreground">· {show.clip_count} clips</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {working ? (
            <Loader2 className="h-4 w-4 animate-spin text-accent" />
          ) : show.status === "completed" ? (
            <CheckCircle2 className="h-4 w-4 text-accent" />
          ) : (
            <XCircle className="h-4 w-4 text-primary" />
          )}
          <button
            data-testid={`delete-show-${show.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(show.id); }}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {show.status === "failed" && (
        <div className="mt-3 space-y-2">
          {show.error && <p className="font-mono text-[10px] leading-snug text-primary/80">{show.error}</p>}
          <div className="flex gap-2">
            <button
              data-testid={`retry-show-${show.id}`}
              onClick={(e) => { e.stopPropagation(); onRetry(show.id); }}
              className="flex items-center gap-1.5 border border-accent/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-accent transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <RotateCw className="h-3 w-3" /> Retry
            </button>
            <button
              data-testid={`skip-show-${show.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(show.id); }}
              className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Trash2 className="h-3 w-3" /> Skip
            </button>
          </div>
        </div>
      )}

      {working && (
        <div className="mt-3">
          <div className="h-1 w-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-500"
              style={{ width: `${show.progress || 4}%` }}
            />
          </div>
          <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
            <Radio className="h-3 w-3 animate-pulse text-primary" /> {show.stage_message}
          </p>
        </div>
      )}
    </motion.div>
  );
};
